import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { API_REQUEST } from '../helpers/request';
import UploadModal from '../components/quote/uploadModal';
import { Plus, Upload } from 'lucide-react';
import ProjectItem from '../components/dashboard/ProjectItem';
import { MAX_FILES } from '../constants';

// MUI Components for filtering
import { Box, Typography, Menu, MenuItem, Chip } from '@mui/material';
import { FilterList } from '@mui/icons-material';

// Filter options - Fixed mapping
const FILTER_OPTIONS = {
  MOST_RECENT: 'most_recent',
  NAME_ASC: 'name_asc',
  NAME_DESC: 'name_desc',
  DATE_CREATED_NEWEST: 'date_newest',
  DATE_CREATED_OLDEST: 'date_oldest',
};

// API parameter mapping
const API_SORT_MAPPING = {
  [FILTER_OPTIONS.MOST_RECENT]: { sort: 'project_activity', order: 'desc' },
  [FILTER_OPTIONS.NAME_ASC]: { sort: 'project_name', order: 'asc' },
  [FILTER_OPTIONS.NAME_DESC]: { sort: 'project_name', order: 'desc' },
  [FILTER_OPTIONS.DATE_CREATED_NEWEST]: {
    sort: 'project_created',
    order: 'desc',
  },
  [FILTER_OPTIONS.DATE_CREATED_OLDEST]: {
    sort: 'project_created',
    order: 'asc',
  },
};

const FILTER_LABELS = {
  [FILTER_OPTIONS.MOST_RECENT]: 'Most Recent Activity',
  [FILTER_OPTIONS.NAME_ASC]: 'Project Name (A-Z)',
  [FILTER_OPTIONS.NAME_DESC]: 'Project Name (Z-A)',
  [FILTER_OPTIONS.DATE_CREATED_NEWEST]: 'Date Created (Newest)',
  [FILTER_OPTIONS.DATE_CREATED_OLDEST]: 'Date Created (Oldest)',
};

const EmptyState = ({ onUploadClick }) => (
  <div className="empty-state-container">
    <div className="empty-state">
      <div className="upload-icon">
        <Upload size={48} strokeWidth={1} />
      </div>
      <h3>You don&apos;t have any quotes yet in this project</h3>
      <p>Upload up to {MAX_FILES} documents today</p>
      <button onClick={onUploadClick} className="upload-button">
        UPLOAD QUOTES NOW
      </button>
    </div>
  </div>
);

EmptyState.propTypes = {
  onUploadClick: PropTypes.func.isRequired,
};

export default function DashboardPage() {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filter state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilter, setActiveFilter] = useState(FILTER_OPTIONS.MOST_RECENT);

  // Load saved filter from localStorage
  useEffect(() => {
    const savedFilter = localStorage.getItem('projectDashboardFilter');
    if (savedFilter && Object.values(FILTER_OPTIONS).includes(savedFilter)) {
      setActiveFilter(savedFilter);
    }
  }, []);

  const fetchProjects = async (filter = activeFilter) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();

      // Get API parameters from mapping
      const apiParams = API_SORT_MAPPING[filter];
      if (apiParams) {
        params.append('sort', apiParams.sort);
        params.append('order', apiParams.order);
      }

      const response = await API_REQUEST.get(`/projects?${params.toString()}`);
      if (API_REQUEST.isSuccess(response.status)) {
        setProjects(response.data);
      }
    } catch (_error) {
      // Swallow error; UI will remain in loading state until retry or refresh
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleProjectDelete = (projectId) => {
    setProjects((currentProjects) =>
      currentProjects.filter((project) => project.id !== projectId)
    );
  };

  const handleAddPackage = (projectId, packages) => {
    setSelectedProjectId(Number(projectId));
    setSelectedPackages(packages);
    setShowUploadModal(true);
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setSelectedProjectId(null);
    setSelectedPackages([]);
  };

  // Filter handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (filter) => {
    setActiveFilter(filter);

    // Save to localStorage for persistence
    localStorage.setItem('projectDashboardFilter', filter);

    // Fetch projects with new sorting
    fetchProjects(filter);
    handleFilterClose();
  };

  const handleClearFilters = () => {
    const defaultFilter = FILTER_OPTIONS.MOST_RECENT;
    setActiveFilter(defaultFilter);
    localStorage.setItem('projectDashboardFilter', defaultFilter);
    fetchProjects(defaultFilter);
  };

  const hasCustomFilter = activeFilter !== FILTER_OPTIONS.MOST_RECENT;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Projects</h1>
          <p>
            You have {projects.length} project{projects.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedProjectId(null);
            setSelectedPackages([]);
            setShowUploadModal(true);
          }}
          className="new-button"
        >
          <Plus className="plus-icon" />
          <span>New</span>
        </button>
      </div>

      {/* Filter Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          p: 2,
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList color="action" />
          <Typography variant="body2" color="text.secondary">
            Sort by:
          </Typography>
          <Chip
            label={FILTER_LABELS[activeFilter]}
            onClick={handleFilterClick}
            variant={hasCustomFilter ? 'filled' : 'outlined'}
            color={hasCustomFilter ? 'primary' : 'default'}
            clickable
            onDelete={hasCustomFilter ? handleClearFilters : undefined}
          />
        </Box>
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
      >
        {Object.entries(FILTER_LABELS).map(([key, label]) => (
          <MenuItem
            key={key}
            selected={activeFilter === key}
            onClick={() => handleFilterSelect(key)}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading projects...</p>
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          onUploadClick={() => {
            setSelectedProjectId(null);
            setSelectedPackages([]);
            setShowUploadModal(true);
          }}
        />
      ) : (
        <div className="projects-list">
          {projects.map((project) => (
            <ProjectItem
              key={project.id}
              project={project}
              onAddPackage={handleAddPackage}
              onProjectDelete={handleProjectDelete}
            />
          ))}
        </div>
      )}

      <UploadModal
        show={showUploadModal}
        setShow={handleCloseModal}
        existingProjectId={selectedProjectId}
        existingPackages={selectedPackages}
      />
    </div>
  );
}
